import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Button, Card, Grid, message, Modal, Space, Table, Tabs, Tag} from 'antd';
import {DeleteOutlined, EyeOutlined, PlusOutlined, ReloadOutlined, TeamOutlined} from '@ant-design/icons';
import {
  createGroup,
  deleteGroup,
  deleteGroupBatch,
  getGroups,
  GROUP_TYPE_OPTIONS,
  updateGroup,
  updateApiGroupMembers,
  updateMenuGroupMembers,
  updateUserGroupMembers
} from '../api/groupApi';
import ManagementHeader from './ManagementHeader';
import useApiPermission from '../hooks/useApiPermission';
import PermissionButton from './common/PermissionButton';
import GroupCreateModal from './GroupCreateModal';
import GroupDetailDrawer from './GroupDetailDrawer';

const TYPE_COLOR = { USER_GROUP: 'cyan', MENU_GROUP: 'blue', API_GROUP: 'geekblue' };
const TYPE_LABEL = { USER_GROUP: '사용자그룹', MENU_GROUP: '메뉴그룹', API_GROUP: 'API그룹' };

const GroupManagement = () => {
  const screens = Grid.useBreakpoint();
  const buttonSize = screens.xs ? 'small' : 'middle';

  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [filterType, setFilterType] = useState();
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRecord, setDrawerRecord] = useState(null);

  const { canCall, loading: permLoading } = useApiPermission();
  const canRead = canCall(getGroups);
  const canCreate = canCall(createGroup);
  const canEdit = canCall(updateGroup);
  const canDeleteSingle = canCall(deleteGroup);
  const canDeleteMulti = canCall(deleteGroupBatch);
  const canEditUserMembers = canCall(updateUserGroupMembers);
  const canEditMenuMembers = canCall(updateMenuGroupMembers);
  const canEditApiMembers = canCall(updateApiGroupMembers);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (permLoading) return;
      if (!canRead) {
        setGroups([]);
        message.warning('그룹 조회 권한이 없습니다.');
        return;
      }

      const res = await getGroups(filterType);
      const list = Array.isArray(res?.data) ? res.data : [];
      if (!Array.isArray(res?.data)) {
        message.warning('그룹 응답 형식이 올바르지 않습니다.');
      }
      setGroups(list);

      if (list.length) {
        setSelectedKeys((prev) => prev.filter((key) => list.some((g) => g.groupId === key)));
      } else {
        setSelectedKeys([]);
      }
    } catch (err) {
      console.error('그룹 목록 조회 실패:', err);
      message.error('그룹 목록 조회에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [filterType, permLoading, canRead]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Drawer 레코드 동기화를 별도로 처리
  useEffect(() => {
    if (!drawerRecord || groups.length === 0) return;
    
    const updated = groups.find((g) => g.groupId === drawerRecord.groupId);
    if (updated) {
      // 실제로 변경된 경우에만 업데이트 (무한 루프 방지)
      if (JSON.stringify(updated) !== JSON.stringify(drawerRecord)) {
        setDrawerRecord(updated);
      }
    } else if (drawerOpen) {
      // 삭제된 경우 Drawer 닫기
      setDrawerOpen(false);
      setDrawerRecord(null);
    }
  }, [groups, drawerRecord?.groupId, drawerOpen]);

  const handleOpenDetail = useCallback((record) => {
    setDrawerRecord(record);
    setDrawerOpen(true);
  }, []);

  const columns = useMemo(() => [
    {
      title: '타입',
      dataIndex: 'groupType',
      key: 'groupType',
      width: 120,
      sorter: (a, b) => (a.groupType || '').localeCompare(b.groupType || ''),
      render: (value) => (
        <Tag color={TYPE_COLOR[value] || 'default'}>{TYPE_LABEL[value] || value}</Tag>
      )
    },
    {
      title: '그룹명',
      dataIndex: 'groupNm',
      key: 'groupNm',
      ellipsis: true,
      sorter: (a, b) => (a.groupNm || '').localeCompare(b.groupNm || '')
    },
    {
      title: '대상수',
      key: 'targetCount',
      width: 90,
      sorter: (a, b) => (a.trgtIdList?.length || 0) - (b.trgtIdList?.length || 0),
      render: (_, record) => record.trgtIdList?.length || 0,
      responsive: ['md']
    },
    {
      title: '설명',
      dataIndex: 'groupDsctn',
      key: 'groupDsctn',
      ellipsis: true,
      sorter: (a, b) => (a.groupDsctn || '').localeCompare(b.groupDsctn || ''),
      responsive: ['lg']
    },
    {
      title: '작업',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          onClick={(event) => {
            event.stopPropagation();
            handleOpenDetail(record);
          }}
        >
          상세
        </Button>
      )
    }
  ], [handleOpenDetail]);

  const openCreate = useCallback(() => {
    if (permLoading || !canCreate) {
      message.warning('생성 권한이 없습니다.');
      return;
    }
    setCreateOpen(true);
  }, [permLoading, canCreate]);

  const handleBatchDelete = useCallback(() => {
    if (!selectedKeys.length) {
      message.warning('삭제할 그룹을 선택하세요.');
      return;
    }
    if (permLoading || !canDeleteMulti) {
      message.warning('일괄 삭제 권한이 없습니다.');
      return;
    }

    Modal.confirm({
      title: '선택 삭제',
      content: `선택된 ${selectedKeys.length}개 그룹을 삭제하시겠습니까?`,
      okText: '삭제',
      okType: 'danger',
      cancelText: '취소',
      onOk: async () => {
        try {
          await deleteGroupBatch(selectedKeys);
          message.success('삭제 완료');
          setSelectedKeys([]);
          if (drawerRecord && selectedKeys.includes(drawerRecord.groupId)) {
            setDrawerOpen(false);
            setDrawerRecord(null);
          }
          await fetchData();
        } catch (err) {
          console.error('일괄 삭제 실패:', err);
          message.error('일괄 삭제에 실패했습니다.');
        }
      }
    });
  }, [selectedKeys, permLoading, canDeleteMulti, drawerRecord, fetchData]);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setDrawerRecord(null);
  }, []);

  const handleAfterUpdate = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const handleAfterDelete = useCallback((deletedGroup) => {
    setSelectedKeys((prev) => prev.filter((key) => key !== deletedGroup?.groupId));
    setDrawerOpen(false);
    setDrawerRecord(null);
    fetchData();
  }, [fetchData]);

  const headerActions = (
    <Space wrap size={8} style={{ width: '100%' }}>
      <PermissionButton
        type="primary"
        icon={<PlusOutlined />}
        onClick={openCreate}
        require={createGroup}
        size={buttonSize}
      >
        {screens.xs ? '생성' : '그룹 생성'}
      </PermissionButton>
      <PermissionButton
        danger
        icon={<DeleteOutlined />}
        disabled={!selectedKeys.length}
        onClick={handleBatchDelete}
        require={deleteGroupBatch}
        size={buttonSize}
      >
        선택삭제
      </PermissionButton>
      <PermissionButton
        icon={<ReloadOutlined />}
        onClick={fetchData}
        require={getGroups}
        loading={loading}
        size={buttonSize}
      >
        새로고침
      </PermissionButton>
    </Space>
  );

  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: setSelectedKeys
  };

  const tabItems = [
    {
      key: 'ALL',
      label: '전체',
      children: (
        <Table
          size="small"
          rowKey="groupId"
          columns={columns}
          dataSource={groups}
          pagination={{ pageSize: 15, showSizeChanger: false }}
          loading={loading}
          rowSelection={rowSelection}
          onRow={(record) => ({
            onClick: () => handleOpenDetail(record)
          })}
          locale={{ emptyText: '그룹이 없습니다.' }}
          scroll={{ x: 'max-content' }}
        />
      )
    },
    {
      key: 'USER_GROUP',
      label: '사용자그룹',
      children: (
        <Table
          size="small"
          rowKey="groupId"
          columns={columns}
          dataSource={groups}
          pagination={{ pageSize: 15, showSizeChanger: false }}
          loading={loading}
          rowSelection={rowSelection}
          onRow={(record) => ({
            onClick: () => handleOpenDetail(record)
          })}
          locale={{ emptyText: '사용자그룹이 없습니다.' }}
          scroll={{ x: 'max-content' }}
        />
      )
    },
    {
      key: 'MENU_GROUP',
      label: '메뉴그룹',
      children: (
        <Table
          size="small"
          rowKey="groupId"
          columns={columns}
          dataSource={groups}
          pagination={{ pageSize: 15, showSizeChanger: false }}
          loading={loading}
          rowSelection={rowSelection}
          onRow={(record) => ({
            onClick: () => handleOpenDetail(record)
          })}
          locale={{ emptyText: '메뉴그룹이 없습니다.' }}
          scroll={{ x: 'max-content' }}
        />
      )
    },
    {
      key: 'API_GROUP',
      label: 'API그룹',
      children: (
        <Table
          size="small"
          rowKey="groupId"
          columns={columns}
          dataSource={groups}
          pagination={{ pageSize: 15, showSizeChanger: false }}
          loading={loading}
          rowSelection={rowSelection}
          onRow={(record) => ({
            onClick: () => handleOpenDetail(record)
          })}
          locale={{ emptyText: 'API그룹이 없습니다.' }}
          scroll={{ x: 'max-content' }}
        />
      )
    }
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ManagementHeader
        icon={<TeamOutlined />}
        title="그룹 관리"
        actions={headerActions}
        description="그룹(사용자/메뉴/API)을 생성하고 상세 정보는 Drawer에서 확인하세요."
      />
      <Card styles={{ body: { padding: 0 } }}>
        <Tabs
          activeKey={filterType || 'ALL'}
          onChange={(key) => setFilterType(key === 'ALL' ? undefined : key)}
          items={tabItems}
          style={{ padding: '0 16px' }}
        />
      </Card>

      <GroupCreateModal
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onSuccess={async () => {
          message.success('그룹이 생성되었습니다.');
          setCreateOpen(false);
          await fetchData();
        }}
      />

      <GroupDetailDrawer
        open={drawerOpen}
        group={drawerRecord}
        onClose={handleDrawerClose}
        onDeleted={handleAfterDelete}
        onUpdated={handleAfterUpdate}
        canEdit={canEdit}
        canDelete={canDeleteSingle}
        canEditUserMembers={canEditUserMembers}
        canEditMenuMembers={canEditMenuMembers}
        canEditApiMembers={canEditApiMembers}
        permLoading={permLoading}
      />
    </div>
  );
};

export default GroupManagement;
